/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    IOrderTemplateActionsData,
    IOrderTemplateListViewProps,
    ISingleOrderTemplateViewProps
} from '@msdyn365-commerce-modules/order-template';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    getPayloadObject,
    getTelemetryAttributes,
    INodeProps,
    IPopupProductsProps,
    ITelemetryContent,
    Module,
    Node,
    PopupProducts,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import CreateNewTemplateButtonComponent from './components/create-order-template.component';
import * as React from 'react';

import { IOrderTemplateListProps, IOrderTemplateListResources } from '../definition-extensions/order-template-list.ext.props.autogenerated';
import {
    addOrderTemplateToCartStatus,
    ILinesAddedToCartDialogResources,
    IOrderTemplateNameDialogResources,
    LinesAddedToCartDialog
} from '@msdyn365-commerce/components';
import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import classnames from 'classnames';
import { getCartState } from '@msdyn365-commerce/global-state';
import { IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import _ from 'lodash';
import { mergeTemplateLinesToCart } from '../../../utils/orderTemplateUtils';

/**
 * Interface for OrderTemplateImage.
 */
interface IOrderTemplateImage {
    props: {
        children: React.ReactElement[];
    };
}

export interface IOrderTemplateActionsProps extends IComponentProps<IOrderTemplateActionsData> {
    addToCartText: string;
    className: string;
    data: IOrderTemplateActionsData;
    id: string;
    typeName: string;
    context: ICoreContext;
    telemetryContent?: ITelemetryContent;
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    dialogStrings: {
        renameTemplateButtonText: string;
        deleteTemplateButtonText: string;
        closeWindowButtonText: string;
        addToCartFailureMessage: string;
        addToCartSuccessMessage: string;
        addToCartProcessMessage: string;
        orderTemplateErrorMessage: string;
        linesAddedToCartDialogResources: ILinesAddedToCartDialogResources;
    };
}

/**
 * To render order template.
 * @param orderTemplate - The order template view props.
 * @param orderTemplateNodeProps - The order template node props.
 * @returns The JSX Element.
 */
const renderOrderTemplate = (
    orderTemplate: ISingleOrderTemplateViewProps,
    props: React.PropsWithChildren<IOrderTemplateListViewProps & IOrderTemplateListProps<IOrderTemplateListResources>>,
    orderTemplateNodeProps?: INodeProps
): JSX.Element | null => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from module file
    const { key, addToCartButton, orderTemplateImage, orderTemplateStatusMessage, removeButton, orderTemplateName } = orderTemplate;

    const addToCartButtonProps = (_.get(addToCartButton, 'props.children.props') as unknown) as IOrderTemplateActionsProps;

    const orderTemplateImageElement: IOrderTemplateImage = orderTemplateImage as React.ReactElement;
    const currentImageListLength = orderTemplateImageElement.props.children.length;
    const defaultNumberImagesInTile = 4;

    if (currentImageListLength < defaultNumberImagesInTile) {
        for (let index = 0; index < defaultNumberImagesInTile - currentImageListLength; index++) {
            orderTemplateImageElement.props.children.push(<picture />);
        }
    }
    const shouldDisplayErrorMessage = addToCartButtonProps.data.orderTemplate.orderTemplateLines.some(line => line.shouldDisable);

    return (
        <Node key={key} className={orderTemplateNodeProps!.className} {...orderTemplateNodeProps}>
            {orderTemplateName}
            {orderTemplateImage}
            {/* {addToCartButton} */}
            <div className='ms-order-template__add-to-bag-container'>
                {shouldDisplayErrorMessage && (
                    <p className='ms-order-template__add-to-bag-container__error-message'>Template contains a product with an error.</p>
                )}
                {_getOrderTemplateAddToCartDialog(addToCartButtonProps)}
            </div>
            {removeButton}
            {orderTemplateStatusMessage}
        </Node>
    );
};

const _getOrderTemplateAddToCartDialog = (addToCartButtonProps: IOrderTemplateActionsProps): React.ReactNode => {
    const [errorMessage, setErrorMessage] = React.useState('');
    const [linesAddedToCartDialogOpen, setLinesAddedToCartDialogOpen] = React.useState(false);
    const [uploadStatus, setUploadStatus] = React.useState<addOrderTemplateToCartStatus>('WAITING');
    const [modalOpen, setModalOpen] = React.useState(false);
    const [addedLinesCount, setAddedLinesCount] = React.useState(0);
    const {
        data: { orderTemplate },
        typeName,
        id,
        context,
        telemetryContent,
        dialogStrings,
        isMultipleCatalogFeatureEnabledInHq,
        addToCartText
    } = addToCartButtonProps;

    const {
        // closeWindowButtonText,
        addToCartFailureMessage,
        addToCartSuccessMessage,
        addToCartProcessMessage,
        // orderTemplateErrorMessage,
        linesAddedToCartDialogResources
    } = dialogStrings;

    const closeLinesAddedToCartDialog = () => {
        setErrorMessage('');
        setLinesAddedToCartDialogOpen(false);
    };

    const _getModalText = (
        status: addOrderTemplateToCartStatus,
        addToCartFailureMessage: string,
        addToCartSuccessMessage: string,
        addToCartProcessMessage: string,
        errorMsg: string
    ) => {
        if (status === 'LOADING') {
            return `${addToCartProcessMessage}...`;
        }

        if (status === 'SUCCESS') {
            return addToCartSuccessMessage;
        }

        if (status === 'FAILED') {
            return errorMsg !== null ? errorMsg : addToCartFailureMessage;
        }

        return '';
    };

    const modalText = _getModalText(uploadStatus, addToCartFailureMessage, addToCartSuccessMessage, addToCartProcessMessage, errorMessage);
    const processStatusClass = uploadStatus.toLowerCase();
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.AddOrderTemplateToCart);
    const addOrderTemplateAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    const popupProps: IPopupProductsProps = {
        dialogStrings,
        className: 'msc-add-order-template-to-cart',
        processStatusClass,
        text: modalText,
        modalOpen,
        setModalOpen
    };

    const renderModalPopup = <PopupProducts {...popupProps} />;

    const products = orderTemplate.orderTemplateLines.map(line => line.fullProduct).filter(fullProduct => fullProduct) as FullProduct[];

    return (
        <>
            {renderModalPopup}
            <div className='ms-order-template__add-to-bag-button-div'>
                <button
                    className={classnames(
                        'ms-order-template__add-to-bag-container__add-to-bag-button',
                        orderTemplate.totalLines === 0 || orderTemplate.orderTemplateLines.some(line => line.shouldDisable)
                            ? 'disabled'
                            : ''
                    )}
                    aria-label={addToCartText}
                    disabled={orderTemplate.totalLines === 0 || orderTemplate.orderTemplateLines.some(line => line.shouldDisable)}
                    type='button'
                    onClick={async () => {
                        setUploadStatus('LOADING');
                        setModalOpen(true);
                        try {
                            await handleAddToCart(addToCartButtonProps, setAddedLinesCount, setErrorMessage, setModalOpen, setUploadStatus);
                            setLinesAddedToCartDialogOpen(true);
                            setUploadStatus('SUCCESS');
                        } catch (error) {
                            setUploadStatus('FAILED');
                            setErrorMessage('An error occurred while adding items to the cart');
                        }
                    }}
                    {...addOrderTemplateAttribute}
                >
                    {addToCartText}
                </button>
            </div>
            <LinesAddedToCartDialog
                className='msc-lines-added-to-cart-dialog'
                id={id}
                typeName={typeName}
                context={context}
                dialogStrings={linesAddedToCartDialogResources}
                data={{
                    linesCount: addedLinesCount,
                    orderTemplate: orderTemplate.productList,
                    products
                }}
                modalOpen={linesAddedToCartDialogOpen}
                onClose={closeLinesAddedToCartDialog}
                isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq || false}
                {...(isMultipleCatalogFeatureEnabledInHq && { catalogId: 0 })}
            />
        </>
    );
};

const handleAddToCart = async (
    props: IOrderTemplateActionsProps,
    setAddedLinesCount: React.Dispatch<React.SetStateAction<number>>,
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
    setUploadStatus: React.Dispatch<React.SetStateAction<addOrderTemplateToCartStatus>>
) => {
    const {
        data: { orderTemplate },
        context,
        isMultipleCatalogFeatureEnabledInHq
    } = props;

    const cartState = await getCartState(context.actionContext);
    const cart = cartState.cart;
    if (cart.CartLines?.some(line => line.IsInvoiceLine)) {
        setErrorMessage('Cannot add items to a cart containing an invoice.');
        setUploadStatus('FAILED');
        return;
    }

    setAddedLinesCount(0);
    const count = await mergeTemplateLinesToCart(
        orderTemplate.productList.Id,
        context.actionContext,
        cart.Id,
        cart.Version,
        cart.CartLines ?? [],
        isMultipleCatalogFeatureEnabledInHq,
        context.telemetry
    );
    setAddedLinesCount(prev => prev + count);
    await cartState.refreshCart({});
    setModalOpen(false);
};

const _reloadPage = async () => {
    window.location.reload();
};

const _generateOrderTemplateNameDialogResources = (resources: IOrderTemplateListResources): IOrderTemplateNameDialogResources => {
    return {
        orderTemplateHeaderLabel: resources.createOrderTemplateHeader,
        orderTemplateTitleLabel: resources.orderTemplateTitle,
        orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
        orderTemplateDescription: resources.createOrderTemplateDescription,

        defaultOrderTemplateName: resources.defaultOrderTemplateName,

        orderTemplateButtonLabel: resources.createOrderTemplateButtonText,
        orderTemplateCancelButtonLabel: resources.cancelNewOrderTemplateCreationButtonText
    };
};

/**
 * Order Template List View.
 * @param props - The view props.
 * @returns The JSX Element.
 */
export const OrderTemplateListViewComponent: React.FC<IOrderTemplateListViewProps &
    IOrderTemplateListProps<IOrderTemplateListResources>> = props => {
    const {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from module file
        OrderTemplateList,
        OrderTemplates,
        OrderTemplate,
        status,
        statusMessage,
        heading,
        orderTemplates,
        createOrderTemplateButton,
        templateHeaderStatusMessage
    } = props;
    // console.log(createOrderTemplateButton);
    return (
        <Module {...OrderTemplateList}>
            {heading}
            {status !== 'SUCCESS' && statusMessage}
            {status === 'EMPTY' && (
                <CreateNewTemplateButtonComponent
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    data={{}}
                    createOrderTemplateText={props.resources.createNewTemplateButtonText}
                    onOrderTemplateCreated={_reloadPage}
                    createOrderTemplateDialogResources={_generateOrderTemplateNameDialogResources(props.resources)}
                />
            )}
            <Node className=''>
                {status === 'SUCCESS' && (!OrderTemplates || !orderTemplates || !ArrayExtensions.hasElements(orderTemplates)) ? (
                    <div className='ms-order-template__empty-list'>
                        <h2 className='ms-order-template__empty-list-heading'>{props.resources.headingForEmptyOrderTemplateList}</h2>
                        <p className='ms-order-template__empty-list-text'>{props.resources.textForEmptyOrderTemplateList}</p>
                        {createOrderTemplateButton}
                    </div>
                ) : (
                    createOrderTemplateButton
                )}
                {templateHeaderStatusMessage}
            </Node>
            {OrderTemplates && orderTemplates && ArrayExtensions.hasElements(orderTemplates) && (
                <Node {...OrderTemplates}>
                    {orderTemplates.map(orderTemplate => {
                        return renderOrderTemplate(orderTemplate, props, OrderTemplate);
                    })}
                </Node>
            )}
        </Module>
    );
};

export default OrderTemplateListViewComponent;
